<template>

  <div class="mt-5 d-flex">

    <div v-if="showIds" style="flex: 0 0 100px; padding-right: 10px">
      <o-text-field
        label="CDR ID"
        type="number"
        :model-value="formState?.ID"
        disabled
        density="compact"
        persistent-placeholder
      />
    </div>

    <div class="flex-grow-1">
      <o-text-field
        density="compact"
        :label="costFieldLabel"
        prefix="$"
        v-model.number="v$.channelCost.$model"
        :validator="v$.channelCost"
        type="number"
        min="0"
        step="0.01"
        clearable
        :readonly="readonly"
      />
    </div>

  </div>

</template>

<script setup lang="ts">

import { computed } from 'vue'
import type { PropType } from 'vue'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { minValue } from '../utils/i18n-validators'
import { isEmpty } from '../validation'
// @ts-ignore: don't complain that dollarAmount isn't TypeScript
import dollarAmount from '../rules/dollarAmount'

// misc
import ChannelRegistry from '../channels/ChannelRegistry'
import type { RateFormChannelRateFormState, RateFormFormState } from '../utils/rateTypes'
import { ChannelFacility } from '../models/ChannelFacility'

const props = defineProps({

  tiered: {
    type: Boolean,
    default: false,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  showIds: {
    type: Boolean,
    default: false,
  },

  // The entire list of DRs in the RateSet
  allDefaultRates: {
    type: Array as PropType<RateFormFormState[]>,
    default: () => [],
  },

  channelFacility: {
    type: ChannelFacility,
  },

})

const costFieldLabel = computed<string>(() => {

  if (!formState.value) {
    return ''
  }

  const channel = ChannelRegistry.find(formState.value.channelFacility.channel)
  const channelName = channel ? channel.titleShort : formState.value.channelFacility.channel
  return `${channelName} (${formState.value.channelFacility.externalFacilityID})`

})

/**
 * Form state and rules
 */

const formState = defineModel<RateFormChannelRateFormState>()

const thisCdr = formState.value

const thisDr = computed<RateFormFormState | null>((() => {
  if (!thisCdr) {
    return null
  }
  return props.allDefaultRates.find(dr => dr.channelDefaultRates.includes(thisCdr)) ?? null
}))

const thisDrDuration = computed<null|number>((() => {
  if (!thisDr.value) {
    return null
  }
  return drDurationInHours(thisDr.value)
}))

const otherDrs = computed<any[]>(() => {
  return props.allDefaultRates.filter(dr => dr !== thisDr.value)
})

const shorterDrs = computed<any[]>(() => {
  if (!thisDrDuration.value) {
    return []
  }
  return otherDrs.value.filter(otherDr => {
    const otherDrDuration = drDurationInHours(otherDr)
    return !isEmpty(otherDrDuration) && (otherDrDuration as number) < (thisDrDuration.value as number)
  })
})

const shorterAndMoreExpensiveDrs = computed<any[]>(() => {
  if (!thisCdr || !thisCdr.channelCost) {
    return []
  }
  return shorterDrs.value.filter(shorterDr => {
    const shorterCdr = shorterDr.channelDefaultRates.find((cdr: RateFormChannelRateFormState) => cdr.channelFacility.ID === thisCdr.channelFacility.ID)
    if (!shorterCdr) {
      return false
    }
    const shorterCdrEffectiveCost = cdrEffectiveCost(shorterCdr, shorterDr)
    if (!shorterCdrEffectiveCost) {
      return false
    }
    return shorterCdrEffectiveCost > (thisCdr.channelCost as number)
  })
})

const formRules = {
  channelCost: {
    minValue: minValue(0.01),
    dollarAmount: helpers.withMessage('Must be a valid dollar amount', dollarAmount),
    notCheaperThanShorterTier: helpers.withMessage('Can\'t be cheaper than a shorter tier', (_value) => {

      if (!props.tiered) {
        return true
      }

      return shorterAndMoreExpensiveDrs.value.length === 0

    }),
    spotheroCannotBeMoreThan250: helpers.withMessage('SpotHero rates can\'t exceed $250', (value: any) => {
      return props.channelFacility?.channel !== 'spothero' || value <= 250
    }),
  },
}

// @ts-ignore
const v$ = useVuelidate(formRules, formState)

const hasError = computed<boolean>(() => v$.value.$errors.length > 0)
defineExpose({ hasError })

/**
 * Miscellaneous
 */

function drDurationInHours(dr: RateFormFormState): null | number {

  if (isEmpty(dr.perQuantity) || isEmpty(dr.perUnit)) {
    return null
  }

  return dr.perQuantity as number * (dr.perUnit as string === 'day' ? 24 : 1)

}

function cdrEffectiveCost(cdr: RateFormChannelRateFormState, dr: RateFormFormState): null | number {

  let effectiveCost = null

  if (!isEmpty(cdr.channelCost)) {
    effectiveCost = cdr.channelCost as number
  } else if (!isEmpty(dr.defaultCost)) {
    effectiveCost = dr.defaultCost as number
  }

  return effectiveCost

}

</script>
